import { useCallback } from 'react';
import {
  Badge,
  BadgeSizes,
  BadgeVariants,
  Button,
  ButtonSizes,
  ButtonVariants,
  TableCellTypes,
  Tooltip,
} from '@carboledger/ui';
import { CellText, makeSerialCell } from '@/components/common';
import { CERTIFICATE_TYPE_TONE, MATERIAL_DECLARATION_SETTINGS_TONE } from '@/config/statusTones';
import { useTranslations } from '@/hooks';
import { SITE_MATERIAL_COLUMNS } from './sites.constants';

const renderCertificateType = (ctx) => {
  const { certificateType, certificateTypeLabel } = ctx.row;
  if (!certificateType) return <CellText>-</CellText>;

  return (
    <Badge variant={CERTIFICATE_TYPE_TONE[certificateType]} size={BadgeSizes.sm}>
      {certificateTypeLabel}
    </Badge>
  );
};

const makeNotApplicableCell = (t, field) => {
  const renderCell = (ctx) => (
    <CellText>{ctx.row[field] ?? t('MATERIALS_NOT_APPLICABLE')}</CellText>
  );
  return renderCell;
};

const makeDeclarationSettingsCell = (t) => {
  const renderDeclarationSettings = (ctx) => {
    const { declarationSettings, declarationSettingsLabel } = ctx.row;
    return (
      <Badge
        variant={MATERIAL_DECLARATION_SETTINGS_TONE[declarationSettings] ?? BadgeVariants.default}
        size={BadgeSizes.sm}
        withDot
      >
        {declarationSettingsLabel ?? t('MATERIALS_NOT_APPLICABLE')}
      </Badge>
    );
  };
  return renderDeclarationSettings;
};

const MaterialRowActions = ({ row, onRemove }) => {
  const t = useTranslations();

  const handleRemove = useCallback(() => onRemove(row), [onRemove, row]);

  return (
    <Tooltip content={t('MATERIALS_REMOVE_LOCKED')} disabled={!row.isInUse}>
      <Button
        variant={ButtonVariants.tertiary}
        size={ButtonSizes.sm}
        disabled={row.isInUse}
        onClick={handleRemove}
      >
        {t('MATERIALS_REMOVE_FROM_TERMINAL')}
      </Button>
    </Tooltip>
  );
};

const makeActionsCell = ({ onRemove }) => {
  const renderActions = (ctx) => <MaterialRowActions row={ctx.row} onRemove={onRemove} />;
  return renderActions;
};

export const buildSiteMaterialsColumns = (t, { onRemove }) => [
  {
    id: SITE_MATERIAL_COLUMNS.serialNo,
    header: t('FIELD_SERIAL_NO'),
    type: TableCellTypes.custom,
    width: 80,
    cell: makeSerialCell(),
  },
  {
    id: SITE_MATERIAL_COLUMNS.materialName,
    header: t('FIELD_MATERIAL_NAME'),
    type: TableCellTypes.text,
    width: 160,
    pinnable: true,
  },
  {
    id: SITE_MATERIAL_COLUMNS.materialCode,
    header: t('FIELD_MATERIAL_ID'),
    type: TableCellTypes.text,
    width: 140,
  },
  {
    id: SITE_MATERIAL_COLUMNS.referenceId,
    header: t('FIELD_REFERENCE_ID'),
    type: TableCellTypes.text,
    width: 140,
  },
  {
    id: SITE_MATERIAL_COLUMNS.materialType,
    header: t('FIELD_MATERIAL_TYPE'),
    type: TableCellTypes.text,
    width: 140,
  },
  {
    id: SITE_MATERIAL_COLUMNS.certificateType,
    header: t('FIELD_CERTIFICATION_TYPE'),
    type: TableCellTypes.custom,
    width: 150,
    cell: renderCertificateType,
  },
  {
    id: SITE_MATERIAL_COLUMNS.materialRole,
    header: t('FIELD_MATERIAL_ROLE'),
    type: TableCellTypes.custom,
    width: 140,
    cell: makeNotApplicableCell(t, SITE_MATERIAL_COLUMNS.materialRole),
  },
  {
    id: SITE_MATERIAL_COLUMNS.process,
    header: t('FIELD_ASSOCIATED_PROCESS'),
    type: TableCellTypes.text,
    width: 220,
  },
  {
    id: SITE_MATERIAL_COLUMNS.declarationSettings,
    header: t('FIELD_DECLARATION_SETTINGS'),
    type: TableCellTypes.custom,
    width: 170,
    cell: makeDeclarationSettingsCell(t),
  },
  {
    id: SITE_MATERIAL_COLUMNS.declarationUnit,
    header: t('FIELD_DECLARATION_UNIT'),
    type: TableCellTypes.text,
    width: 140,
  },
  {
    id: SITE_MATERIAL_COLUMNS.workingUnit,
    header: t('FIELD_WORKING_UNIT'),
    type: TableCellTypes.text,
    width: 130,
  },
  {
    id: SITE_MATERIAL_COLUMNS.conversionFactor,
    header: t('FIELD_CONVERSION_FACTOR'),
    type: TableCellTypes.text,
    width: 190,
  },
  {
    id: SITE_MATERIAL_COLUMNS.energyContent,
    header: t('FIELD_ENERGY_CONTENT_PER_UNIT'),
    type: TableCellTypes.text,
    width: 150,
  },
  {
    id: SITE_MATERIAL_COLUMNS.actions,
    header: t('SITES_COL_ACTIONS'),
    type: TableCellTypes.custom,
    width: 180,
    isActions: true,
    reorderable: false,
    resizable: false,
    cell: makeActionsCell({ onRemove }),
  },
];
